import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import PollenDotsNav from '@/components/PollenDotsNav';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, MapPin, ExternalLink, Check, AlertCircle, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth } from '@/App';

const TYPE_FILTERS = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'city', label: 'City', match: l => l.type === 'city' },
  { key: 'hotel', label: 'Hotel', match: l => l.type === 'hotel' },
  { key: 'guide', label: 'Guide', match: l => l.type === 'guide' },
  { key: 'driver', label: 'Driver', match: l => l.type === 'driver' },
  { key: 'activity', label: 'Activity', match: l => l.type === 'activity' },
  // Tour-level enroute stops persisted via upsert-stop; surfaced here so
  // operators can audit / pin / merge them just like any other place.
  { key: 'enroute_stop', label: 'Enroute Stop', match: l => l.type === 'enroute_stop' },
];

/**
 * PollenDotsLocations — Super-Admin view of every place in `pollen_locations`.
 * For each one, pasting a Google Maps URL (or bare "lat, lng") sets the precise
 * coordinates so the map (once re-enabled) points exactly at the right spot.
 */
const PollenDotsLocations = () => {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const load = async () => {
    setLoading(true);
    try {
      const t = localStorage.getItem('session_token');
      const res = await fetch(`${API}/pollen/locations`, {
        credentials: 'include',
        headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (res.ok) setLocations(await res.json());
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Deeplink from the Profile / Listings flag rows: ?focus=<location_id>
  // scrolls + highlights the matching row so the user lands directly on
  // the entry that needs fixing.
  const [params] = useSearchParams();
  const focusId = params.get('focus');
  useEffect(() => {
    if (!focusId || loading) return;
    const el = document.querySelector(`[data-testid="location-row-${focusId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.classList.add('ring-2', 'ring-amber-400', 'rounded-md');
      const t = setTimeout(() => el.classList.remove('ring-2', 'ring-amber-400', 'rounded-md'), 2200);
      return () => clearTimeout(t);
    }
  }, [focusId, loading, locations.length]);

  const activeFilter = TYPE_FILTERS.find(f => f.key === typeFilter) || TYPE_FILTERS[0];
  const filtered = (locations || [])
    .filter(activeFilter.match)
    .filter(l => !q || (l.name || '').toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => {
      // Missing-coords first, then alphabetical
      const ma = a.lat == null ? 0 : 1;
      const mb = b.lat == null ? 0 : 1;
      if (ma !== mb) return ma - mb;
      return (a.name || '').localeCompare(b.name || '');
    });

  // Per-type counts for the filter chip badges
  const typeCounts = TYPE_FILTERS.reduce((acc, f) => {
    acc[f.key] = (locations || []).filter(f.match).length;
    return acc;
  }, {});

  const missing = locations.filter(l => l.lat == null).length;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-4 pb-20">
        <PollenDotsNav active="locations" />
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Locations</h1>
            <p className="text-sm text-muted-foreground">Paste a Google Maps URL (or copy-pasted "lat, lng") to set precise coordinates for each place.</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={missing > 0 ? 'destructive' : 'outline'} className="text-[11px]" data-testid="locations-missing-count">
              {missing > 0 ? `${missing} missing coords` : 'All pinned ✓'}
            </Badge>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-[11px] gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={async () => {
                if (!window.confirm("Scan and remove route-noise location entries (e.g. 'Guwahati-Shergon-Tawang...')? This is safe — only fictitious city rows that look like multi-hop routes are removed.")) return;
                try {
                  const t = localStorage.getItem('session_token');
                  const res = await fetch(`${API}/pollen/locations/purge-route-noise`, {
                    method: 'POST',
                    credentials: 'include',
                    headers: t ? { Authorization: `Bearer ${t}` } : {},
                  });
                  const data = await res.json();
                  if (!res.ok) throw new Error(data.detail || 'Purge failed');
                  if (data.deleted === 0) {
                    toast.success('No route-noise entries found — DB is already clean.');
                  } else {
                    toast.success(`Removed ${data.deleted} route-noise entries`, {
                      description: data.samples?.slice(0, 5).join(', ') || '',
                    });
                    load();
                  }
                } catch (e) {
                  toast.error(e.message);
                }
              }}
              data-testid="locations-purge-route-noise"
              title="One-shot: deletes any city entries that look like multi-hop transport routes"
            >
              <AlertCircle className="w-3 h-3" /> Purge route noise
            </Button>
          </div>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search locations…" value={q} onChange={e => setQ(e.target.value)} className="pl-9" data-testid="locations-search" />
        </div>

        <div className="flex flex-wrap gap-1.5" data-testid="locations-type-filters">
          {TYPE_FILTERS.map(f => {
            const active = typeFilter === f.key;
            return (
              <Button
                key={f.key}
                size="sm"
                variant={active ? 'default' : 'outline'}
                className="h-7 text-[11px] px-2.5 gap-1.5"
                onClick={() => setTypeFilter(f.key)}
                data-testid={`locations-filter-${f.key}`}
              >
                {f.label}
                <Badge variant={active ? 'secondary' : 'outline'} className="text-[9px] px-1 py-0">
                  {typeCounts[f.key] || 0}
                </Badge>
              </Button>
            );
          })}
        </div>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">All places ({filtered.length})</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
            ) : filtered.length === 0 ? (
              <div className="p-12 text-center text-sm text-muted-foreground">No locations found.</div>
            ) : (
              <div className="divide-y" data-testid="locations-list">
                {filtered.map(loc => <SafeLocationRow key={loc.id} loc={loc} onSaved={load} />)}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

const LocationRow = ({ loc, onSaved }) => {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const lat = typeof loc.lat === 'number' && !Number.isNaN(loc.lat) ? loc.lat : null;
  const lng = typeof loc.lng === 'number' && !Number.isNaN(loc.lng) ? loc.lng : null;
  const hasCoords = lat != null && lng != null;
  // Notes editor — enroute stops carry first-write-wins canonical notes
  // that operators wanted to be able to amend from the Locations DB
  // page (the only spot that's allowed to override the original tour's
  // notes). Other location types don't surface a notes editor.
  const isStop = loc.type === 'enroute_stop';
  const [notes, setNotes] = useState(loc.notes || '');
  const [editingNotes, setEditingNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  const save = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const t = localStorage.getItem('session_token');
      const res = await fetch(`${API}/pollen/locations/${loc.id}/set-coords`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
        body: JSON.stringify({ google_maps_url: url.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not set coordinates');
      toast.success(`${loc.name} → ${data.lat.toFixed(4)}, ${data.lng.toFixed(4)}`);
      setUrl('');
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const t = localStorage.getItem('session_token');
      const res = await fetch(`${API}/pollen/locations/${loc.id}/set-notes`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(t ? { Authorization: `Bearer ${t}` } : {}),
        },
        body: JSON.stringify({ notes: notes.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not update notes');
      toast.success('Notes updated');
      setEditingNotes(false);
      onSaved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div className="p-3 flex flex-col gap-2" data-testid={`location-row-${loc.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-start gap-2 sm:w-64 shrink-0">
        <MapPin className={`w-4 h-4 mt-0.5 shrink-0 ${hasCoords ? 'text-teal-600' : 'text-rose-500'}`} />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{loc.name}</p>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            <Badge variant="outline" className="text-[10px]">{loc.type === 'enroute_stop' ? 'Enroute Stop' : loc.type}</Badge>
            {loc.subtype && (
              <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">
                {loc.subtype}
              </Badge>
            )}
            {hasCoords ? (
              <span className="text-[10px] text-muted-foreground font-mono">{lat.toFixed(4)}, {lng.toFixed(4)}</span>
            ) : (
              <span className="text-[10px] text-rose-600 flex items-center gap-0.5"><AlertCircle className="w-3 h-3" /> no coords</span>
            )}
          </div>
        </div>
      </div>
      <div className="flex-1 flex items-center gap-2">
        <Input
          placeholder='Paste Google Maps URL or "25.28, 91.73"'
          value={url}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); }}
          className="h-8 text-xs"
          data-testid={`location-url-input-${loc.id}`}
        />
        <Button size="sm" onClick={save} disabled={busy || !url.trim()} className="h-8 text-xs" data-testid={`location-save-${loc.id}`}>
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
        </Button>
        {(hasCoords || /^https?:\/\//i.test((loc.google_maps_url || '').trim())) && (
          <a
            href={
              /^https?:\/\//i.test((loc.google_maps_url || '').trim())
                ? loc.google_maps_url.trim()
                : `https://www.google.com/maps/?q=${lat},${lng}`
            }
            target="_blank"
            rel="noopener noreferrer"
            title="Open this pin in Google Maps"
            className="h-8 px-2 inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-xs font-medium"
            data-testid={`location-open-${loc.id}`}
          >
            <ExternalLink className="w-3 h-3" />Open
          </a>
        )}
      </div>
      </div>
      {/* Notes — canonical first-write-wins for enroute stops, shown
          (and editable) only on enroute_stop rows so we don't crowd
          city/hotel rows that don't use this field. */}
      {isStop && (
        editingNotes ? (
          <div className="flex items-start gap-2 pl-6">
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="flex-1 text-xs px-2 py-1.5 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-amber-400"
              placeholder="Canonical notes that autocomplete will hydrate into future tours"
              data-testid={`location-notes-input-${loc.id}`}
            />
            <Button size="sm" onClick={saveNotes} disabled={savingNotes} className="h-7 text-xs px-2" data-testid={`location-notes-save-${loc.id}`}>
              {savingNotes ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setNotes(loc.notes || ''); setEditingNotes(false); }} className="h-7 text-xs px-2">Cancel</Button>
          </div>
        ) : (
          <button
            type="button"
            className="pl-6 text-left text-[11px] text-muted-foreground italic hover:text-foreground transition-colors flex items-start gap-1.5"
            onClick={() => setEditingNotes(true)}
            data-testid={`location-notes-edit-${loc.id}`}
            title="Click to edit canonical notes"
          >
            <Sparkles className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
            <span className="line-clamp-2">{(loc.notes || '').trim() || '+ Add canonical notes (shown in tours when this stop is picked)'}</span>
          </button>
        )
      )}
    </div>
  );
};

export default PollenDotsLocations;

// Per-row error boundary so one malformed `pollen_locations` document
// (e.g. an old row whose lat got persisted as a string) can NEVER blank
// the entire Locations page. The bad row renders a single inline error
// chip with a "Set coords" prompt; everything else keeps working.
class SafeLocationRow extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err) { console.error('LocationRow crashed:', err); }
  render() {
    if (this.state.err) {
      const l = this.props.loc || {};
      return (
        <div className="p-3 flex items-center gap-2 text-xs text-rose-600" data-testid={`location-row-error-${l.id || 'unknown'}`}>
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="truncate">
            <strong>{l.name || '(unnamed)'}</strong> — couldn't render this row. Edit it via the Database tab or paste a Google Maps URL to repair.
          </span>
        </div>
      );
    }
    return <LocationRow {...this.props} />;
  }
}
